<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  emits: ['punched'],
  data() {
    return {
      busy: false,
      now: new Date(),
      timer: null,
      lastAction: null,   // 'in' | 'out'
      lastTime: null,
    };
  },
  computed: {
    timeStr() {
      return this.now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    secStr() {
      const s = this.now.getSeconds();
      return s < 10 ? '0' + s : '' + s;
    },
    dateStr() {
      return this.now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
  },
  mounted() {
    this.timer = setInterval(() => { this.now = new Date(); }, 1000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    getLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Location is not supported by this browser.'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          (err) => {
            const msg = err.code === 1
              ? 'Location permission denied. Please allow location access to punch.'
              : 'Could not get your location. Try again.';
            reject(new Error(msg));
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });
    },

    async punch(type) {
      if (this.busy) return;
      this.busy = true;
      this.setLoading(true);
      try {
        const loc = await this.getLocation();
        const url = type === 'in' ? '/hrms/attendances/punch-in' : '/hrms/attendances/punch-out';
        const res = await axios.post(url, loc);
        this.$swalToast(res.data.message || (type === 'in' ? 'Punched in successfully.' : 'Punched out successfully.'));
        this.lastAction = type;
        this.lastTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        this.$emit('punched');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.busy = false;
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="pw-card mb-4">
    <span class="pw-blob pw-blob-1"></span>
    <span class="pw-blob pw-blob-2"></span>

    <div class="pw-inner">
      <!-- Left: clock -->
      <div class="pw-time-block">
        <div class="pw-live"><span class="pw-dot"></span> LIVE</div>
        <div class="pw-clock">
          {{ timeStr }}<span class="pw-sec">:{{ secStr }}</span>
        </div>
        <div class="pw-date">
          <i class="ti ti-calendar-event"></i> {{ dateStr }}
        </div>
      </div>

      <!-- Right: actions -->
      <div class="pw-action-block">
        <div v-if="lastAction" class="pw-last">
          <i class="ti" :class="lastAction === 'in' ? 'ti-login-2' : 'ti-logout-2'"></i>
          Last {{ lastAction === 'in' ? 'punched in' : 'punched out' }} at <strong class="ms-1">{{ lastTime }}</strong>
        </div>
        <div class="pw-buttons">
          <button class="pw-btn pw-in" :disabled="busy" @click="punch('in')">
            <span class="pw-btn-ico"><i class="ti ti-fingerprint"></i></span>
            <span class="pw-btn-txt"><small>Start your day</small>Punch In</span>
          </button>
          <button class="pw-btn pw-out" :disabled="busy" @click="punch('out')">
            <span class="pw-btn-ico"><i class="ti ti-door-exit"></i></span>
            <span class="pw-btn-txt"><small>End your day</small>Punch Out</span>
          </button>
        </div>
        <div class="pw-note">
          <i class="ti ti-map-pin"></i> Your location is verified against your branch when you punch.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pw-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #9333ea 100%);
  box-shadow: 0 18px 40px -12px rgba(79, 70, 229, .45);
  color: #fff;
}
.pw-blob { position: absolute; border-radius: 50%; opacity: .18; background: #fff; }
.pw-blob-1 { width: 160px; height: 160px; top: -80px; right: 452px; }
.pw-blob-2 { width: 160px; height: 160px; bottom: -80px; left: 30%; opacity: .12; }

.pw-inner {
  position: relative; z-index: 1;
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 20px; padding: 24px 28px;
}

.pw-time-block { display: flex; flex-direction: column; }
.pw-live {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 700; letter-spacing: 1.5px; opacity: .85; margin-bottom: 6px;
}
.pw-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74,222,128,.7); animation: pw-pulse 1.6s infinite;
}
@keyframes pw-pulse {
  0% { box-shadow: 0 0 0 0 rgba(74,222,128,.6); }
  70% { box-shadow: 0 0 0 8px rgba(74,222,128,0); }
  100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
}
.pw-clock { font-size: 40px; font-weight: 800; line-height: 1; letter-spacing: .5px; }
.pw-sec { font-size: 20px; font-weight: 600; opacity: .65; }
.pw-date { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; opacity: .9; margin-top: 8px; }

.pw-action-block { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
.pw-last {
  font-size: 11.5px; opacity: .92; background: rgba(255,255,255,.14);
  padding: 5px 12px; border-radius: 20px; display: inline-flex; align-items: center; gap: 6px;
}
.pw-buttons { display: flex; gap: 12px; }
.pw-btn {
  display: inline-flex; align-items: center; gap: 11px; border: none; cursor: pointer;
  padding: 12px 20px; border-radius: 14px;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
}
.pw-btn:disabled { opacity: .6; cursor: not-allowed; }
.pw-btn:not(:disabled):hover { transform: translateY(-2px); }
.pw-btn-ico {
  width: 38px; height: 38px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center; font-size: 19px;
}
.pw-btn-txt {
  display: flex; flex-direction: column; align-items: flex-start;
  font-size: 15px; font-weight: 700; line-height: 1.15; text-align: left;
}
.pw-btn-txt small { font-size: 10px; font-weight: 500; opacity: .7; }

.pw-in { background: #fff; color: #4f46e5; box-shadow: 0 8px 20px -6px rgba(0,0,0,.3); }
.pw-in .pw-btn-ico { background: #eef2ff; color: #4f46e5; }
.pw-in:not(:disabled):hover { box-shadow: 0 12px 26px -6px rgba(0,0,0,.4); }

.pw-out { background: rgba(255,255,255,.16); color: #fff; }
.pw-out .pw-btn-ico { background: rgba(255,255,255,.2); color: #fff; }
.pw-out:not(:disabled):hover { background: rgba(255,255,255,.26); }

.pw-note { font-size: 11px; opacity: .8; display: inline-flex; align-items: center; gap: 5px; }

@media (max-width: 768px) {
  .pw-inner { flex-direction: column; align-items: stretch; }
  .pw-action-block { align-items: stretch; }
  .pw-buttons { flex-direction: column; }
  .pw-btn { justify-content: flex-start; }
  .pw-clock { font-size: 34px; }
}
</style>